import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ChevronDown, Check, X, Shield, BadgeCheck, Sparkles, Droplets,
  Wand2, Layers, Cog, CircleDot, PawPrint, Lightbulb, Wind, Truck, Wrench,
  FlaskConical, ScanEye, CalendarCheck, ThumbsUp, ArrowRight, MapPin, Phone, Mail,
  Trash2, Car, Clock, Crown, Sofa,
} from "lucide-react";
import {
  PACKAGES, ADDONS, AUTHORITY_PLUS_VEHICLE, REASONS, PROCESS_STEPS, FAQS, GALLERY, BUSINESS,
} from "@/data/content";
import { HOME } from "@/constants/testIds";
import Logo from "@/components/Logo";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const ICONS = {
  Sparkles, Sofa, Droplets, Shield, Wand2, Layers, Cog, CircleDot, PawPrint,
  Lightbulb, Wind, Truck, Wrench, FlaskConical, ScanEye, BadgeCheck,
  CalendarCheck, ThumbsUp, Trash2, MapPin,
};

function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Overline({ children, tone = "text-blue-400" }) {
  return (
    <div className={`font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.3em] ${tone} mb-3`}>
      {children}
    </div>
  );
}

/* ============ 1. FIRST VIEWPORT — SERVICE SELECTION ============ */
function ServiceHero() {
  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050914] px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-6 sm:pb-12 flex items-center"
    >
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute inset-0 noise-overlay" />
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto w-full">
        <FadeIn>
          <div className="flex items-center gap-3 mb-3 sm:mb-6">
            <Logo size={40} />
            <div className="h-7 w-px bg-white/15" />
            <div className="font-mono text-[9px] sm:text-[11px] uppercase tracking-[0.25em] text-blue-400">
              Phoenix, AZ · Fully Mobile
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.05}>
          <h1 className="font-display text-[2rem] sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white leading-[0.95]">
            Authority Detailing Co.
          </h1>
          <p className="mt-1.5 sm:mt-3 font-mono text-[8px] sm:text-xs uppercase tracking-[0.28em] text-white/45">
            Setting the Standard · Leading the Industry · Earning Your Trust
          </p>
          <p className="mt-2.5 sm:mt-4 text-sm sm:text-lg text-white/70 max-w-xl leading-relaxed">
            Professional mobile auto detailing and trash bin cleaning — delivered to your
            driveway with transparent, up-front pricing.
          </p>
        </FadeIn>

        <FadeIn delay={0.12}>
          <div className="mt-5 sm:mt-10">
            <Overline>What service are you looking for?</Overline>
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-5">
              <Link
                to="/book"
                className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-white/10 bg-[#0B132B] p-4 sm:p-7 transition-colors duration-300 hover:border-blue-500/60"
                data-testid="hero-picker-auto"
              >
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <div className="h-9 w-9 sm:h-11 sm:w-11 grid place-items-center rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 mb-2.5 sm:mb-4">
                      <Car className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <div className="font-display text-xl sm:text-3xl font-bold text-white tracking-tight">
                      Mobile Auto Detailing
                    </div>
                    <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-white/60 leading-relaxed">
                      3rd · 2nd · 1st Degree packages. From $150. We come to you.
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-blue-400 shrink-0 mt-1 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              </Link>

              <Link
                to="/bin-cleaning"
                className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-white/10 bg-[#0B132B] p-4 sm:p-7 transition-colors duration-300 hover:border-blue-500/60"
                data-testid="hero-picker-bin"
              >
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <div className="h-9 w-9 sm:h-11 sm:w-11 grid place-items-center rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 mb-2.5 sm:mb-4">
                      <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <div className="font-display text-xl sm:text-3xl font-bold text-white tracking-tight">
                      Trash Bin Cleaning
                    </div>
                    <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-white/60 leading-relaxed">
                      One-Time · Monthly · Bi-Weekly. From $15 per bin.
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-blue-400 shrink-0 mt-1 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              </Link>
            </div>

            <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] sm:text-xs text-white/50">
              <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="hero-call">
                <Phone className="h-3.5 w-3.5 text-blue-400" /> {BUSINESS.phone}
              </a>
              <span className="text-white/20">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-blue-400" /> Licensed &amp; Insured
              </span>
              <span className="text-white/20">·</span>
              <span>{BUSINESS.hours}</span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ============ 2. REAL WORK GALLERY — moved up, asymmetric ============ */
function GallerySection() {
  const [tab, setTab] = useState("all");
  const items = tab === "all" ? GALLERY : GALLERY.filter((g) => g.category.toLowerCase() === tab);
  // Asymmetric "tetris" spans for visual rhythm
  const spanFor = (i) =>
    i === 0
      ? "col-span-2 row-span-2"
      : i === 3
        ? "md:col-span-2"
        : "";

  return (
    <section id="gallery" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 border-b border-white/5">
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-12">
            <div>
              <Overline>Real Work</Overline>
              <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
                Actual details. Before &amp; after.
              </h2>
              <p className="mt-3 text-sm sm:text-base text-white/60 max-w-xl">
                Every photo below is real work from Authority Detailing Co. — no stock photography, no filters on the results.
              </p>
            </div>
            <div className="flex gap-2">
              {[
                { id: "all", label: "All" },
                { id: "exterior", label: "Exterior" },
                { id: "interior", label: "Interior" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`text-xs sm:text-sm px-4 py-2 rounded-md border transition-colors duration-200 ${
                    tab === t.id
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-transparent border-white/10 text-white/70 hover:border-white/30 hover:text-white"
                  }`}
                  data-testid={`gallery-filter-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </FadeIn>

        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[150px] sm:auto-rows-[180px] md:auto-rows-[210px] gap-2.5 sm:gap-3 [grid-auto-flow:dense]">
          {items.map((g, i) => (
            <FadeIn key={`${g.label}-${i}`} delay={i * 0.05} className={spanFor(i)}>
              <figure
                className="group relative h-full w-full overflow-hidden rounded-lg sm:rounded-xl border border-white/10 bg-[#0B132B]"
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={g.image}
                  alt={`${g.label} — real before and after by Authority Detailing Co.`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050914]/90 via-transparent to-transparent" />
                <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-sm bg-blue-600 text-[9px] font-mono uppercase tracking-[0.2em] text-white">
                  Before / After
                </div>
                <figcaption className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-blue-300">{g.category}</div>
                  <div className="font-display text-sm sm:text-base font-semibold text-white leading-tight">{g.label}</div>
                </figcaption>
              </figure>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.1}>
          <div className="mt-8 sm:mt-10">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-200"
              data-testid="gallery-cta-book"
            >
              Book Your Service <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ============ 3. DETAILING PACKAGES — full presentation ============ */
function InclusionGroup({ title, items, tone = "check" }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">{title}</div>
      <ul className="space-y-1.5 text-xs sm:text-sm text-white/75">
        {items.map((f) => (
          <li key={f} className="flex items-start gap-2">
            {tone === "check" ? (
              <Check className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
            ) : (
              <X className="h-3.5 w-3.5 text-white/30 mt-0.5 shrink-0" />
            )}
            <span className={tone === "check" ? "" : "text-white/45"}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PackageCard({ p, index }) {
  const [open, setOpen] = useState(false);
  return (
    <FadeIn delay={index * 0.08} className="h-full">
      <div
        className={`relative rounded-2xl p-6 sm:p-8 h-full flex flex-col transition-colors duration-300 ${
          p.popular
            ? "bg-[#0B132B] border-2 border-blue-500/70"
            : "bg-[#0B132B] border border-white/10 hover:border-white/25"
        }`}
        data-testid={HOME.packageCard(p.id)}
      >
        {p.popular && (
          <div className="absolute -top-3 left-6 bg-blue-600 text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-sm">
            Most Popular
          </div>
        )}

        <Overline>{p.tier}</Overline>
        <h3 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight">{p.label}</h3>
        <p className="text-sm text-white/60 mt-2 leading-relaxed">{p.purpose}</p>

        <div className="mt-5 flex items-end gap-3 border-y border-white/10 py-4">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">Sedan / Coupe</div>
            <div className="font-display text-4xl font-bold text-white">${p.sedan}</div>
          </div>
          <div className="h-10 w-px bg-white/10" />
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">SUV / Truck</div>
            <div className="font-display text-2xl font-bold text-white/85">${p.suv}</div>
          </div>
        </div>

        <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-blue-400">
          <Clock className="h-3.5 w-3.5" /> {p.time}
        </div>

        <ul className="mt-5 space-y-2 text-sm text-white/80">
          {p.features.slice(0, 5).map((f) => (
            <li key={f} className="flex items-start gap-2">
              <Check className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
          data-testid={`package-details-toggle-${p.id}`}
          aria-expanded={open}
        >
          {open ? "Hide full breakdown" : "See everything included"}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-5 pt-5 border-t border-white/10 space-y-5">
                {p.paintPrep && <InclusionGroup title="Paint Prep" items={p.paintPrep} />}
                {p.paintEnhancement && <InclusionGroup title="Paint Enhancement" items={p.paintEnhancement} />}
                {p.exterior && <InclusionGroup title="Exterior" items={p.exterior} />}
                {p.interior && <InclusionGroup title="Interior" items={p.interior} />}
                {p.interiorNote && (
                  <p className="text-xs sm:text-sm text-white/70 flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" /> {p.interiorNote}
                  </p>
                )}
                {p.notIncluded && <InclusionGroup title="Not Included" items={p.notIncluded} tone="x" />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {p.disclosure && (
          <div className="mt-4 rounded-lg bg-white/[0.04] border border-white/10 p-3.5 text-xs text-white/60 leading-relaxed">
            <span className="font-semibold text-white/85">Important: </span>{p.disclosure}
          </div>
        )}

        <div className="mt-4 text-xs text-white/50 italic flex-1">Best for: {p.bestFor}</div>

        <Link
          to={`/book?pkg=${p.id}`}
          className={`mt-6 inline-flex items-center justify-center gap-2 font-semibold px-6 py-3.5 rounded-md transition-colors duration-200 ${
            p.popular
              ? "bg-blue-600 hover:bg-blue-500 text-white"
              : "bg-white text-[#050914] hover:bg-white/90"
          }`}
          data-testid={HOME.packageSelectBtn(p.id)}
        >
          Select {p.tier} <ArrowRight className="h-4 w-4" />
        </Link>

        {p.flagship && (
          <a
            href="#authority-plus"
            className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
            data-testid="package-authority-plus-link"
          >
            <Crown className="h-3.5 w-3.5" /> Continue into Authority+ after this detail
          </a>
        )}
      </div>
    </FadeIn>
  );
}

function Packages() {
  return (
    <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#070c1c]">
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          <div className="mb-10 sm:mb-14 max-w-3xl">
            <Overline>Mobile Auto Detailing</Overline>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
              Three levels of detail.<br className="hidden sm:block" /> One clear price each.
            </h2>
            <p className="mt-4 text-sm sm:text-base text-white/60 leading-relaxed">
              Every package lists exactly what's included, what it costs for your vehicle type,
              and how long it takes. No surprises on service day.
            </p>
          </div>
        </FadeIn>

        <div className="grid lg:grid-cols-3 gap-5 sm:gap-6 items-start">
          {PACKAGES.map((p, i) => (
            <PackageCard key={p.id} p={p} index={i} />
          ))}
        </div>

        {/* Approved add-ons only */}
        <FadeIn delay={0.1}>
          <div className="mt-12 sm:mt-16">
            <Overline>Add-Ons</Overline>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {ADDONS.map((a) => (
                <div
                  key={a.id}
                  className="lift rounded-xl border border-white/10 bg-[#0B132B] p-5"
                  data-testid={`addon-card-${a.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-base font-semibold text-white leading-tight">{a.name}</div>
                    <div className="font-mono text-[11px] text-blue-400 whitespace-nowrap">{a.priceLabel}</div>
                  </div>
                  <p className="text-xs text-white/55 mt-2 leading-relaxed">{a.desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-white/45">
              Add-ons are selected during booking. "Starting at" pricing is confirmed on site before any work begins.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ============ 4. AUTHORITY+ — THE INTERACTIVE JOURNEY ============ */
const AP_STAGES = [
  {
    n: "01",
    title: "New Customer",
    tagline: "Your vehicle needs real care.",
    detail: "Every Authority+ relationship starts the same way — with a vehicle that needs proper, professional attention.",
  },
  {
    n: "02",
    title: "Initial Detail",
    tagline: "We reset your vehicle.",
    detail: "A 3rd, 2nd, or 1st Degree Detail gets your vehicle properly cleaned, inside and out — the right starting point for real maintenance.",
  },
  {
    n: "03",
    title: "Properly Maintained",
    tagline: "Clean stays clean, easier.",
    detail: "A properly detailed vehicle is easier to maintain when it's maintained consistently. Dirt never gets the chance to take over again.",
  },
  {
    n: "04",
    title: "Authority+",
    tagline: "The exclusive member rate.",
    detail: "Returning customers continue with recurring professional maintenance at the Authority+ rate — instead of paying the full regular 3rd Degree price every visit.",
    plus: true,
  },
];

function AuthorityPlus() {
  const [active, setActive] = useState(1);
  const [veh, setVeh] = useState("sedan");
  const [whoOpen, setWhoOpen] = useState(false);

  const prices = {
    sedan: { label: "Sedan / Coupe", regular: AUTHORITY_PLUS_VEHICLE.regularSedan, member: AUTHORITY_PLUS_VEHICLE.sedan },
    suv: { label: "SUV / Truck", regular: AUTHORITY_PLUS_VEHICLE.regularSuv, member: AUTHORITY_PLUS_VEHICLE.suv },
  };

  return (
    <section id="authority-plus" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-emerald-500/25 bg-[#07120d] p-5 sm:p-10 lg:p-14">
          <div className="absolute inset-0 noise-overlay" />
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

          <div className="relative">
            <FadeIn>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="h-9 w-9 grid place-items-center rounded-md bg-emerald-500/15 border border-emerald-500/30">
                  <Crown className="h-4 w-4 text-emerald-400" />
                </div>
                <Overline tone="text-emerald-400">Authority+ · Maintenance Membership</Overline>
              </div>
              <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white max-w-2xl">
                Get it detailed.{" "}
                <span className="text-emerald-400">Keep it maintained.</span>
              </h2>
              <p className="mt-4 text-sm sm:text-base text-white/65 max-w-2xl leading-relaxed">
                A properly detailed vehicle is easier to maintain when it's maintained consistently.
                Authority+ is how returning customers keep that just-detailed standard — at an exclusive
                recurring rate. Tap each stage to see how it works.
              </p>
            </FadeIn>

            {/* Interactive journey */}
            <FadeIn delay={0.08}>
              <div className="mt-8 sm:mt-10 grid md:grid-cols-4 gap-2.5 sm:gap-3 relative" data-testid="ap-journey">
                <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-white/10 via-emerald-500/40 to-emerald-500/60 -z-0" />
                {AP_STAGES.map((s, i) => {
                  const isActive = active === i;
                  return (
                    <button
                      key={s.n}
                      type="button"
                      onClick={() => setActive(i)}
                      className={`relative text-left rounded-xl border p-4 sm:p-5 transition-colors duration-300 ${
                        isActive
                          ? s.plus
                            ? "border-emerald-400 bg-emerald-500/15"
                            : "border-blue-500 bg-blue-500/10"
                          : "border-white/10 bg-[#0B132B]/80 hover:border-white/25"
                      }`}
                      data-testid={`ap-step-${i}`}
                      aria-pressed={isActive}
                    >
                      <div className={`font-mono text-[10px] tracking-[0.25em] ${s.plus ? "text-emerald-400" : "text-blue-400"}`}>
                        {s.n}
                      </div>
                      <div className="font-display text-base sm:text-lg font-bold text-white mt-1 leading-tight">
                        {s.title}
                      </div>
                      <div className="text-[11px] sm:text-xs text-white/55 mt-1">{s.tagline}</div>
                    </button>
                  );
                })}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                  className={`mt-4 rounded-xl border p-5 sm:p-6 text-sm sm:text-base leading-relaxed ${
                    AP_STAGES[active].plus
                      ? "border-emerald-500/40 bg-emerald-500/10 text-white/85"
                      : "border-white/10 bg-[#0B132B] text-white/75"
                  }`}
                  data-testid="ap-stage-detail"
                >
                  <span className="font-semibold text-white">{AP_STAGES[active].title}: </span>
                  {AP_STAGES[active].detail}
                </motion.div>
              </AnimatePresence>
            </FadeIn>

            {/* Member pricing — interactive comparison */}
            <FadeIn delay={0.12}>
              <div className="mt-10 sm:mt-12">
                <Overline tone="text-emerald-400">Member Pricing · Recurring 3rd Degree Visits</Overline>
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  {Object.entries(prices).map(([key, pr]) => {
                    const isSel = veh === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setVeh(key)}
                        className={`text-left rounded-xl border p-5 sm:p-6 transition-colors duration-300 ${
                          isSel
                            ? "border-emerald-400 bg-emerald-500/15"
                            : "border-white/10 bg-[#0B132B]/80 hover:border-emerald-500/40"
                        }`}
                        data-testid={`ap-price-${key}`}
                        aria-pressed={isSel}
                      >
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{pr.label}</div>
                        <div className="mt-3 flex items-baseline gap-3">
                          <span className="text-white/35 line-through text-lg">${pr.regular}</span>
                          <span className="font-display text-4xl sm:text-5xl font-bold text-emerald-400">${pr.member}</span>
                          <span className="text-xs text-white/45">/ visit</span>
                        </div>
                        <div className="mt-2 text-[11px] sm:text-xs text-white/55">
                          Regular 3rd Degree ${pr.regular} → Authority+ ${pr.member} every visit
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-white/45">
                  No membership fees. No contracts. Member pricing applies to recurring 3rd Degree
                  maintenance visits after your initial detail.
                </p>
              </div>
            </FadeIn>

            {/* Who it's for + pathways */}
            <FadeIn delay={0.16}>
              <div className="mt-8 sm:mt-10 grid lg:grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/10 bg-[#0B132B]/80 p-5 sm:p-6">
                  <button
                    type="button"
                    onClick={() => setWhoOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-3 text-left"
                    data-testid="ap-who-toggle"
                    aria-expanded={whoOpen}
                  >
                    <span className="font-display text-base sm:text-lg font-bold text-white">Who is Authority+ for?</span>
                    <ChevronDown className={`h-4 w-4 text-emerald-400 transition-transform duration-300 ${whoOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {whoOpen && (
                      <motion.ul
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden mt-4 space-y-2.5 text-sm text-white/75"
                      >
                        {[
                          "Your vehicle has been properly detailed",
                          "You want to keep it consistently maintained",
                          "You prefer recurring professional care",
                          "You want the Authority+ maintenance rate",
                        ].map((t) => (
                          <li key={t} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {t}
                          </li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>

                <div className="rounded-xl border border-white/10 bg-[#0B132B]/80 p-5 sm:p-6">
                  <div className="font-display text-base sm:text-lg font-bold text-white">Every detail leads here.</div>
                  <div className="mt-4 space-y-2">
                    {PACKAGES.map((p) => (
                      <Link
                        key={p.id}
                        to={`/book?pkg=${p.id}`}
                        className="flex items-center justify-between gap-2 rounded-lg border border-white/10 px-3.5 py-2.5 text-xs sm:text-sm text-white/75 hover:border-emerald-500/40 hover:text-white transition-colors duration-200"
                        data-testid={`ap-path-${p.id}`}
                      >
                        <span>{p.tier} — {p.label}</span>
                        <span className="inline-flex items-center gap-1 text-emerald-400 font-semibold whitespace-nowrap">
                          → Authority+
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  to="/book"
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-[#050914] font-bold px-7 py-4 rounded-md transition-colors duration-200"
                  data-testid="ap-cta"
                >
                  Book Your First Detail <ArrowRight className="h-4 w-4" />
                </Link>
                <span className="text-xs text-white/45 max-w-xs">
                  Start with any detail package — Authority+ continues from there.
                </span>
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ 5. WHY US ============ */
function WhyUs() {
  return (
    <section id="why" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#070c1c] border-y border-white/5">
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          <div className="mb-10 sm:mb-14 max-w-3xl">
            <Overline>Why Choose Us</Overline>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
              Professional by design.<br className="hidden sm:block" /> Honest by default.
            </h2>
          </div>
        </FadeIn>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {REASONS.map((r, i) => {
            const Icon = ICONS[r.icon] || Sparkles;
            return (
              <FadeIn key={r.title} delay={i * 0.04}>
                <div className="lift h-full p-5 sm:p-6 rounded-xl bg-[#0B132B] border border-white/10 hover:border-blue-500/40">
                  <Icon className="h-6 w-6 text-blue-400 mb-3" />
                  <div className="font-display text-base font-semibold text-white">{r.title}</div>
                  <div className="text-xs sm:text-sm text-white/60 mt-1.5 leading-relaxed">{r.text}</div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============ 6. PROCESS ============ */
function Process() {
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          <div className="mb-10 sm:mb-14 max-w-3xl">
            <Overline>The Process</Overline>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
              From booking to walk-through.
            </h2>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-5 gap-3 sm:gap-4">
          {PROCESS_STEPS.map((step, i) => (
            <FadeIn key={step.n} delay={i * 0.06}>
              <div className="relative p-5 sm:p-6 rounded-xl bg-[#0B132B] border border-white/10 h-full">
                <div className="font-mono text-blue-400 text-xs tracking-[0.2em]">{step.n}</div>
                <div className="font-display text-base sm:text-lg font-semibold text-white mt-2 leading-tight">{step.title}</div>
                <div className="text-xs sm:text-sm text-white/60 mt-2 leading-relaxed">{step.desc}</div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ 7. FAQ ============ */
function Faq() {
  return (
    <section id="faq" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#070c1c] border-y border-white/5">
      <div className="max-w-3xl mx-auto">
        <FadeIn>
          <div className="mb-8 sm:mb-12">
            <Overline>FAQ</Overline>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
              Questions, answered straight.
            </h2>
          </div>
        </FadeIn>

        <FadeIn delay={0.08}>
          <Accordion type="single" collapsible className="w-full space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="rounded-xl border border-white/10 bg-[#0B132B] px-5"
                data-testid={HOME.faqItem(i)}
              >
                <AccordionTrigger className="text-white text-left text-sm sm:text-base font-semibold hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70 text-sm leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeIn>
      </div>
    </section>
  );
}

/* ============ 8. CONTACT ============ */
function Contact() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", vehicle: "", package: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        vehicle: form.vehicle,
        package: form.package,
        message: form.message,
      });
      toast.success("Message sent — we'll get back to you as soon as we can.");
      setForm({ name: "", phone: "", email: "", vehicle: "", package: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10">
        <FadeIn>
          <Overline>Contact</Overline>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
            Talk to a real person.
          </h2>
          <p className="mt-5 text-sm sm:text-base text-white/70 leading-relaxed max-w-md">
            Call, text, or send a message — you'll hear back directly from the owner.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-4 group">
              <div className="h-12 w-12 grid place-items-center rounded-lg bg-[#0B132B] border border-white/10 group-hover:border-blue-500/50 transition-colors">
                <Phone className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Phone</div>
                <div className="text-white font-semibold">{BUSINESS.phone}</div>
              </div>
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-4 group">
              <div className="h-12 w-12 grid place-items-center rounded-lg bg-[#0B132B] border border-white/10 group-hover:border-blue-500/50 transition-colors">
                <Mail className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Email</div>
                <div className="text-white font-semibold break-all">{BUSINESS.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 grid place-items-center rounded-lg bg-[#0B132B] border border-white/10">
                <MapPin className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Service Area</div>
                <div className="text-white font-semibold">{BUSINESS.area}</div>
              </div>
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <form onSubmit={submit} className="rounded-2xl bg-[#0B132B] border border-white/10 p-6 sm:p-8 space-y-4">
            <Input
              required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full Name" className="bg-white/5 border-white/10 text-white h-12"
              data-testid={HOME.contactName}
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone" className="bg-white/5 border-white/10 text-white h-12"
                data-testid={HOME.contactPhone}
              />
              <Input
                required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email" className="bg-white/5 border-white/10 text-white h-12"
                data-testid={HOME.contactEmail}
              />
            </div>
            <Input
              value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
              placeholder="Vehicle (Year / Make / Model)" className="bg-white/5 border-white/10 text-white h-12"
              data-testid={HOME.contactVehicle}
            />
            <Select value={form.package} onValueChange={(v) => setForm({ ...form, package: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white h-12" data-testid={HOME.contactPackage}>
                <SelectValue placeholder="Interested package" />
              </SelectTrigger>
              <SelectContent>
                {PACKAGES.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.tier} — {p.label}</SelectItem>
                ))}
                <SelectItem value="not_sure">Not sure yet</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us what you need..." rows={5}
              className="bg-white/5 border-white/10 text-white"
              data-testid={HOME.contactMessage}
            />
            <button
              type="submit" disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold py-4 rounded-md transition-colors duration-200"
              data-testid={HOME.contactSubmit}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </FadeIn>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <ServiceHero />
      <GallerySection />
      <Packages />
      <AuthorityPlus />
      <WhyUs />
      <Process />
      <Faq />
      <Contact />
    </main>
  );
}
